// @ts-strict-ignore
import React, { useState } from 'react';
import {
  Box, Button, ButtonVariant, IconName, MultiSelect, Text,
} from '@usealloy/component-library';
import type { MultiSelectProps, Option, SelectedOption } from '@usealloy/component-library';
import type { Action, DataRoute } from 'flow/store/engine/types';
import {
  selectActions,
  selectFocusedAction,
  selectNodeLineageForAnchorNode,
  selectServices,
  selectWorkflow,
  selectDataRoutes,
  selectUniversalAttributes,
  selectDiffMode,
} from 'flow/store/redux/selectors';
import { createDataRoute, removeDataRoute, updateDataRouteValue } from '~/dashboard/views/workflows/store/redux/slice';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { ActionType } from 'flow/store/engine/constants';
import {
  CUSTOM_LIST_COLUMNS_OPTION_KEY,
  filterOutMetadataColumns,
  getActionName,
  isCustomListAction,
} from '~/dashboard/views/workflows/service/actions';
import type { CustomListColumn } from '~/dashboard/views/workflows/service/actions';
import type { SchemaField } from '~/helpers/api-new/modules/workflows/workflowsApi';
import { findCollidingDynamicDataRoute } from '~/dashboard/views/workflows/service/dataRoutes';
import {
  buildActionOptions,
  buildAttributeOptions,
  getDropdownTextStyles,
  getMultiSelectProps,
} from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/Threshold/helpers';
import { resolveServiceOrUniversalAttribute } from 'flow/service/thresholds';
import {
  CategoryInfo,
  RouteActions,
  StyledCategoryContainer,
  StyledDynamicRouteChip,
  StyledLockedRouteValue,
  dynamicRouteChipTokens,
} from './styles';
import DestinationFieldPicker from './DestinationFieldPicker';
import { AttributeWidgetOption } from '../../ThresholdsTab/RuleBuilderContainer/Rule/Threshold/AttributeWidgetOption';
import type { Operation } from 'flow/service/changelog/types';
import { PLACEHOLDER_DIFF_STATUS, withDiffStyles } from '../../diffStyles';

const LENGTH_LIMIT = 60;

// New routes target the anchor node (this tab); TO is only editable from the target's own tab.
const blankDataRoute = (publicId: string, anchorPublicId: string): DataRoute => ({
  publicId,
  sourceId: null,
  targetId: anchorPublicId,
  category: { id: null, name: null },
  attribute: {
    id: null, token: null, displayName: '', attributeName: '',
  },
  aggregationComponents: [],
  target_payload_key: '',
});

// Wraps Thresholds' getMultiSelectProps with the pending-pill placeholder look, then layers
// diff coloring on top so the control reads as added/modified/removed.
function getChipMultiSelectProps(
  action?: Action,
  diffStatus?: Operation | null,
): Partial<MultiSelectProps> {
  const base = getMultiSelectProps({ action, options: { isPreview: false } });
  return withDiffStyles({
    ...base,
    customStyleFunctions: {
      ...base.customStyleFunctions,
      placeholder: () => ({
        display: 'inline-flex',
        width: 'fit-content',
        ...dynamicRouteChipTokens,
      }),
    },
  }, diffStatus);
}

function formatLockedRouteLabel({
  action,
  field,
  includeActionName = true,
}: {
  action?: Action;
  field?: string;
  includeActionName?: boolean;
}): string | undefined {
  if (!action) return undefined;
  if (!field) return getActionName(action);
  return includeActionName ? `${getActionName(action)}:${field}` : field;
}

export default function DynamicRouteRow({
  publicId,
  onSaved,
  onRemoved,
  isDisabled,
}: {
  publicId: string;
  onSaved: (publicId: string) => void;
  onRemoved: (publicId: string) => void;
  isDisabled?: boolean;
}) {
  const dispatch = useAppDispatch();
  const dataRoutes = useAppSelector(selectDataRoutes);
  const actions = useAppSelector(selectActions);
  const services = useAppSelector(selectServices);
  const universalAttributes = useAppSelector(selectUniversalAttributes);
  const workflow = useAppSelector(selectWorkflow);
  const nodeLineage = useAppSelector(selectNodeLineageForAnchorNode);
  const focusedAction = useAppSelector(selectFocusedAction);
  const isInDiffMode = useAppSelector(selectDiffMode);

  const existingDataRoute = dataRoutes[publicId];
  const [draft, setDraft] = useState<DataRoute>(
    existingDataRoute ?? blankDataRoute(publicId, focusedAction.publicId),
  );
  const [isEditing, setIsEditing] = useState(!existingDataRoute);

  const sourceAction = draft.sourceId ? actions[draft.sourceId] : null;
  const targetAction = draft.targetId ? actions[draft.targetId] : null;
  const isTargetEditableHere = draft.targetId === focusedAction.publicId;
  const canEditTo = isEditing && isTargetEditableHere;

  // Custom List has no Chimera schema - its columns are cached on the action instead.
  const isTargetCustomList = isCustomListAction(targetAction);
  const customListColumns = targetAction?.options?.[CUSTOM_LIST_COLUMNS_OPTION_KEY]
    ?.jsonValue as CustomListColumn[] | undefined;
  const customListFields: SchemaField[] | undefined = isTargetCustomList
    ? filterOutMetadataColumns(customListColumns ?? []).map(column => ({
      name: column.name,
      kind: 'primitive' as const,
    }))
    : undefined;

  const isSourceService = sourceAction?.type === ActionType.SERVICE;
  const sourceService = isSourceService ? services[sourceAction.service.internalName] : null;

  const isDraftComplete = Boolean(draft.sourceId)
    && (!isSourceService || Boolean(draft.attribute.id))
    && Boolean(draft.target_payload_key);

  // Tags aren't eligible FROM sources - routing a tag's value into a service payload is out of scope.
  const eligibleSourceActions = Object.fromEntries(
    Object.entries(actions).filter(([, action]) => action.type !== ActionType.TAG),
  );

  const sourceNodeOptions = buildActionOptions({
    actions: eligibleSourceActions,
    sourceAction: targetAction ?? focusedAction,
    services,
    nodeLineage,
    lengthLimit: LENGTH_LIMIT,
    thresholdSide: { actionId: draft.sourceId },
  }).filter(option => (
    isTargetEditableHere || option.value === focusedAction.publicId
  ));

  const attributeOptions = isSourceService && sourceService
    ? buildAttributeOptions({
      service: sourceService,
      workflow,
      universalAttributes,
      allowJsonAttributes: true,
    })
    : [];

  const onSelectSourceNode = (selected: SelectedOption) => {
    const option = selected as Option;
    setDraft(current => ({
      ...current,
      sourceId: option.value,
      attribute: {
        id: null, token: null, displayName: '', attributeName: '',
      },
    }));
  };

  const onSelectAttribute = (selected: SelectedOption) => {
    const option = selected as Option;
    const attribute = resolveServiceOrUniversalAttribute(
      sourceService?.attributes,
      universalAttributes ?? {},
      Number(option.value),
    );
    setDraft(current => ({
      ...current,
      attribute: {
        id: Number(option.value),
        token: attribute?.attribute_token ?? null,
        displayName: option.label,
        attributeName: option.label,
      },
    }));
  };

  const onChangeTargetPayloadKey = (value: string) => {
    setDraft(current => ({ ...current, target_payload_key: value }));
  };

  const onClickConfirm = () => {
    if (!isEditing) {
      setIsEditing(true);
      return;
    }

    const collidingRoute = findCollidingDynamicDataRoute({ dataRoutes, draft });
    if (collidingRoute) {
      dispatch(removeDataRoute({ id: collidingRoute.publicId }));
    }

    if (existingDataRoute) {
      dispatch(updateDataRouteValue({ path: draft.publicId, value: draft }));
    } else {
      dispatch(createDataRoute({ path: draft.publicId, value: draft, expressionPath: '' }));
    }
    setIsEditing(false);
    onSaved(publicId);
  };

  const onClickRemove = () => {
    if (existingDataRoute) {
      dispatch(removeDataRoute({ id: publicId }));
    }
    onRemoved(publicId);
  };

  const lockedFromLabel = formatLockedRouteLabel({
    action: sourceAction,
    field: draft.attribute.id ? draft.attribute.displayName : undefined,
  });
  const lockedToLabel = formatLockedRouteLabel({
    action: targetAction,
    field: draft.target_payload_key || '--',
    includeActionName: !isTargetEditableHere,
  });

  // TODO: hardcoded until the per-route diff status is wired in. The diff-mode guard is
  // redundant once that lookup returns null outside diff mode, and can be dropped then.
  const diffStatus = isInDiffMode ? PLACEHOLDER_DIFF_STATUS : null;

  return (
    <StyledCategoryContainer diffStatus={diffStatus}>
      <CategoryInfo>
        <Text as="span" size={2} weight={400} style={{ color: 'var(--cl-gray-700)' }}>
          From
        </Text>
        <Box justifyContent="flex-start" gap={2}>
          {isEditing ? (
            <>
              <MultiSelect
                {...getChipMultiSelectProps(sourceAction, diffStatus)}
                openMenuOnFocus
                options={sourceNodeOptions}
                selectedOptions={sourceAction
                  ? { label: getActionName(sourceAction), value: sourceAction.publicId }
                  : null}
                placeholder="select"
                onChange={onSelectSourceNode}
                isDisabled={isDisabled}
              />
              {isSourceService && (
                <MultiSelect
                  {...getChipMultiSelectProps(sourceAction, diffStatus)}
                  openMenuOnFocus
                  options={attributeOptions}
                  selectedOptions={draft.attribute.id
                    ? { label: draft.attribute.displayName, value: String(draft.attribute.id) }
                    : null}
                  placeholder="select attribute"
                  onChange={onSelectAttribute}
                  isDisabled={isDisabled}
                  components={{ Option: AttributeWidgetOption }}
                />
              )}
            </>
          ) : (
            <StyledLockedRouteValue style={getDropdownTextStyles(sourceAction)}>
              {lockedFromLabel ?? <StyledDynamicRouteChip>select</StyledDynamicRouteChip>}
            </StyledLockedRouteValue>
          )}
        </Box>
      </CategoryInfo>

      <CategoryInfo>
        <Text as="span" size={2} weight={400} style={{ color: 'var(--cl-gray-700)' }}>
          To
        </Text>
        <Box justifyContent="flex-start" gap={2}>
          {canEditTo && targetAction && (
            <DestinationFieldPicker
              internalName={targetAction.service.internalName}
              value={draft.target_payload_key ?? ''}
              onChange={onChangeTargetPayloadKey}
              customListFields={customListFields}
            />
          )}
          {!canEditTo && (
            <StyledLockedRouteValue>
              {lockedToLabel ?? <StyledDynamicRouteChip>select</StyledDynamicRouteChip>}
            </StyledLockedRouteValue>
          )}
        </Box>
      </CategoryInfo>

      <RouteActions>
        <Button
          icon={isEditing ? IconName.Approved : IconName.Edit}
          variant={ButtonVariant.tertiary}
          disabled={(isEditing && !isDraftComplete) || isDisabled}
          onClick={onClickConfirm}
        />
        <Button
          icon={IconName.Trash}
          variant={ButtonVariant.tertiary}
          onClick={onClickRemove}
          disabled={isDisabled}
        />
      </RouteActions>
    </StyledCategoryContainer>
  );
}
